// ─── Preload queue: max 4 concurrent background loads ─────────────────────────
const preloadQueue = [];
let activePreloads = 0;
const MAX_CONCURRENT = 4;
const HOVER_DWELL_MS = 150;

function flushQueue() {
  while (activePreloads < MAX_CONCURRENT && preloadQueue.length > 0) {
    const img = preloadQueue.shift();
    if (!img || !img.dataset.src) continue;

    activePreloads++;
    loadImage(img)
      .catch(() => {})
      .finally(() => {
        activePreloads--;
        flushQueue();
      });
  }
}

function enqueuePreload(img) {
  if (!img || !img.dataset.src) return;
  if (preloadQueue.includes(img)) return;
  preloadQueue.push(img);
  flushQueue();
}

function preloadImage(img) {
  enqueuePreload(img);
}

function preloadAllHoverImages(images) {
  for (let i = 1; i < images.length; i++) {
    preloadImage(images[i]);
  }
}

// Viewport'ta sadece 2. görseli yükle — koleksiyon sayfasında gereksiz istek önlenir
function preloadNextHoverImage(images) {
  if (images[1]) preloadImage(images[1]);
}

// ─── Load image, return promise ───────────────────────────────────────────────
function loadImage(img) {
  if (!img.dataset.src && img.complete && img.naturalWidth > 0) {
    return Promise.resolve();
  }

  if (img.dataset.src) {
    img.src = img.dataset.src;
    img.srcset = img.dataset.srcset || '';
    img.sizes = img.dataset.sizes || '';
    img.removeAttribute('data-src');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-sizes');
    img.classList.remove('lazy-hover-img');

    return new Promise((resolve, reject) => {
      if (img.complete && img.naturalWidth > 0) {
        resolve();
        return;
      }
      img.addEventListener('load', () => resolve(), { once: true });
      img.addEventListener('error', () => reject(), { once: true });
    });
  }

  if (!img.complete) {
    return new Promise((resolve, reject) => {
      img.addEventListener('load', () => resolve(), { once: true });
      img.addEventListener('error', () => reject(), { once: true });
    });
  }

  return Promise.resolve();
}

function isImageReady(img) {
  return !img.dataset.src && img.complete && img.naturalWidth > 0;
}

class ProductCardHover {
  constructor() {
    this.initializedZones = new WeakSet();

    this.viewportObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          preloadNextHoverImage(entry.target.querySelectorAll('.product-hover-img'));
          this.viewportObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px 300px 0px' }
    );

    this.init();
  }

  init() {
    this.setupExistingZones();

    const observer = new MutationObserver(() => {
      this.setupExistingZones();
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  setupExistingZones() {
    document.querySelectorAll('.product-hover-zones').forEach((zonesContainer) => {
      if (this.initializedZones.has(zonesContainer)) return;

      const container = zonesContainer.closest('.product-hover-container');
      if (!container) return;

      const images = container.querySelectorAll('.product-hover-img');
      if (images.length === 0) return;

      images.forEach((img, index) => {
        img.style.setProperty('opacity', index === 0 ? '1' : '0', 'important');
        img.style.setProperty('z-index', index === 0 ? '2' : '1', 'important');
      });
      this._updateDots(container, 0);

      this.viewportObserver.observe(container);

      const isTouchOnly = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

      if (!isTouchOnly) {
        container.addEventListener('mouseenter', () => {
          preloadAllHoverImages(images);
        });

        zonesContainer.querySelectorAll('.hover-zone').forEach((hoverZone) => {
          hoverZone.addEventListener('mouseenter', () => {
            const zoneIndex = parseInt(hoverZone.dataset.zone, 10);
            clearTimeout(container._hoverTimer);
            container._hoverTimer = setTimeout(() => {
              this._showImageAtIndex(container, zoneIndex, images);
            }, HOVER_DWELL_MS);
          });
        });

        container.addEventListener('mouseleave', () => {
          clearTimeout(container._hoverTimer);
          this._resetToFirst(container, images);
        });
      }

      container.addEventListener('touchstart', (event) => {
        container._touchStartX = event.touches[0].clientX;
        container._touchStartY = event.touches[0].clientY;
        container._touchStartTime = Date.now();
        container._isSwiping = false;
        preloadAllHoverImages(images);
        const currentIndex = parseInt(container.getAttribute('data-current-image'), 10) || 0;
        if (images[currentIndex + 1]) preloadImage(images[currentIndex + 1]);
      }, { passive: true });

      container.addEventListener('touchmove', (event) => {
        if (!container._touchStartX) return;
        const diffX = Math.abs(event.touches[0].clientX - container._touchStartX);
        const diffY = Math.abs(event.touches[0].clientY - container._touchStartY);
        if (diffX > diffY && diffX > 10) container._isSwiping = true;
      }, { passive: true });

      container.addEventListener('touchend', (event) => {
        if (!container._touchStartX || !container._isSwiping) {
          container._touchStartX = null;
          container._isSwiping = false;
          return;
        }

        const diffX = container._touchStartX - event.changedTouches[0].clientX;
        const diffY = Math.abs(container._touchStartY - event.changedTouches[0].clientY);
        const duration = Date.now() - container._touchStartTime;

        if (Math.abs(diffX) > 20 && diffY < 80 && duration < 800) {
          const currentIndex = parseInt(container.getAttribute('data-current-image'), 10) || 0;
          const maxIndex = images.length - 1;
          let newIndex = currentIndex;

          if (diffX > 0 && currentIndex < maxIndex) newIndex = currentIndex + 1;
          else if (diffX < 0 && currentIndex > 0) newIndex = currentIndex - 1;

          if (newIndex !== currentIndex) {
            this._showImageAtIndex(container, newIndex, images);
          }
        }

        container._touchStartX = null;
        container._isSwiping = false;
      }, { passive: true });

      this.initializedZones.add(zonesContainer);
    });
  }

  _revealImage(container, index, images) {
    const activeIndex = parseInt(container.getAttribute('data-current-image'), 10) || 0;
    if (activeIndex !== index) return;

    images.forEach((imgEl, i) => {
      imgEl.style.setProperty('opacity', i === index ? '1' : '0', 'important');
      imgEl.style.setProperty('z-index', i === index ? '2' : '1', 'important');
    });
    this._updateDots(container, index);
  }

  _showImageAtIndex(container, index, images) {
    const currentIndex = parseInt(container.getAttribute('data-current-image'), 10) || 0;
    if (currentIndex === index) return;

    container.setAttribute('data-current-image', index.toString());

    const img = images[index];
    if (!img) return;

    if (images[index + 1]) preloadImage(images[index + 1]);
    if (images[index - 1]) preloadImage(images[index - 1]);

    if (isImageReady(img)) {
      this._revealImage(container, index, images);
      return;
    }

    loadImage(img)
      .then(() => this._revealImage(container, index, images))
      .catch(() => {});
  }

  _resetToFirst(container, images) {
    container.setAttribute('data-current-image', '0');
    images.forEach((img, index) => {
      img.style.setProperty('opacity', index === 0 ? '1' : '0', 'important');
      img.style.setProperty('z-index', index === 0 ? '2' : '1', 'important');
    });
    this._updateDots(container, 0);
  }

  _updateDots(container, activeIndex) {
    container.querySelectorAll('.hover-dot').forEach((dot, index) => {
      dot.classList.toggle('active', index === activeIndex);
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new ProductCardHover());
} else {
  new ProductCardHover();
}
